import { Link, useParams } from 'react-router-dom';
import { useGameSocket } from '../hooks/useGameSocket';
import { useViewerStore } from '../store/viewerStore';
import { ConnectionStatus } from './ConnectionStatus';
import { Scoreboard } from './Scoreboard';
import { GameLog } from './GameLog';
import { PlayerStats } from './PlayerStats';

export function LiveGameViewer() {
  // Get gameId from URL if present (for multi-game support)
  const { gameId } = useParams<{ gameId?: string }>();

  // Connect to socket - join specific game if gameId provided, otherwise auto-join active game
  const { retry } = useGameSocket(gameId);

  const { gameState, gameEnded, noActiveGame, gameFull, connectionStatus, activeTab, setActiveTab } = useViewerStore();

  // Game at capacity
  if (gameFull) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md text-center">
          <div className="text-6xl mb-4">📺</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Game at Capacity</h1>
          <p className="text-gray-600 mb-4">
            {gameFull.message}
          </p>
          <p className="text-sm text-gray-500 mb-6">
            Current viewers: {gameFull.viewerCount} / {gameFull.maxViewers}
          </p>
          <div className="flex flex-col gap-3">
            <button
              onClick={retry}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              Try Again
            </button>
            <Link
              to="/"
              className="text-blue-600 hover:text-blue-700 text-sm"
            >
              ← Back to Dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // No active game - show waiting screen with link back to dashboard
  if (noActiveGame) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md text-center">
          <div className="text-6xl mb-4">🏀</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Waiting for Game</h1>
          <p className="text-gray-600 mb-6">
            No game is currently being streamed. The page will automatically update when a game starts.
          </p>
          <div className="flex items-center justify-center gap-2 text-sm text-gray-500 mb-4">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <span>Connected to server</span>
          </div>
          <div className="flex flex-col gap-3">
            <button
              onClick={retry}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              Check Again
            </button>
            <Link
              to="/"
              className="text-blue-600 hover:text-blue-700 text-sm"
            >
              ← Back to Dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Game ended
  if (gameEnded) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md text-center">
          <div className="text-6xl mb-4">🏁</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Live Stream Stopped</h1>
          <p className="text-gray-500 text-sm mb-2">Game may have ended</p>
          <p className="text-gray-600 mb-6">
            The connection to this game was lost. Try reconnecting or check the dashboard for other live games.
          </p>
          <div className="flex flex-col gap-3">
            <button
              onClick={retry}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              Try to Reconnect
            </button>
            <Link
              to="/"
              className="text-blue-600 hover:text-blue-700 text-sm"
            >
              ← Back to Dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  if (!gameState) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">
            {connectionStatus === 'connecting' ? 'Connecting...' : 'Loading Game...'}
          </h2>
          <p className="text-gray-600 text-sm mb-4">
            {connectionStatus === 'error'
              ? 'Unable to connect to server. Please try again.'
              : 'Please wait while we connect to the game stream.'}
          </p>
          {connectionStatus === 'error' && (
            <div className="flex flex-col gap-3">
              <button
                onClick={retry}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                Retry
              </button>
              <Link
                to="/"
                className="text-blue-600 hover:text-blue-700 text-sm"
              >
                ← Back to Dashboard
              </Link>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 py-2 sm:py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            <Link to="/" className="text-gray-400 hover:text-gray-600">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </Link>
            <span className="text-xl sm:text-2xl">🏀</span>
            <h1 className="text-sm sm:text-lg font-semibold text-gray-800">Live Game</h1>
          </div>
          <ConnectionStatus />
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-7xl mx-auto p-2 sm:p-4">
        {/* Scoreboard */}
        <div className="mb-3 sm:mb-6">
          <Scoreboard
            homeTeam={gameState.homeTeam}
            awayTeam={gameState.awayTeam}
            period={gameState.period}
            clockTime={gameState.clockTime}
            possessionArrow={gameState.possessionArrow}
          />
        </div>

        {/* Mobile tabs */}
        <div className="md:hidden mb-2">
          <div className="flex bg-white rounded-lg shadow p-1">
            <button
              onClick={() => setActiveTab('scoreboard')}
              className={`flex-1 py-1.5 text-xs sm:text-sm font-medium rounded-md transition-colors ${
                activeTab === 'scoreboard'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Stats
            </button>
            <button
              onClick={() => setActiveTab('playbyplay')}
              className={`flex-1 py-1.5 text-xs sm:text-sm font-medium rounded-md transition-colors ${
                activeTab === 'playbyplay'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Play-by-Play
            </button>
          </div>
        </div>

        {/* Desktop: Two-column layout */}
        <div className="hidden md:grid md:grid-cols-2 gap-6 h-[500px]">
          <PlayerStats homeTeam={gameState.homeTeam} awayTeam={gameState.awayTeam} />
          <GameLog entries={gameState.gameLog} />
        </div>

        {/* Mobile: Tab content */}
        <div className="md:hidden h-[350px]">
          {activeTab === 'scoreboard' ? (
            <PlayerStats homeTeam={gameState.homeTeam} awayTeam={gameState.awayTeam} />
          ) : (
            <GameLog entries={gameState.gameLog} />
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="text-center py-2 sm:py-4 text-gray-400 text-xs sm:text-sm">
        Settle the Score
      </footer>
    </div>
  );
}
